/** Allowed tokens for the homepage `mapFilter` query param (comma-separated). */
const MAP_FILTER_TOKENS = new Set(['80kw', '160kw', 'nacs', 'cheapest', 'china', 'european', 'fast', 'slow', 'online']);

/**
 * Parse `mapFilter` query value into map filter state.
 * Supports legacy single value (`80kw`) and comma lists (`80kw,cheapest,china`).
 * @param {string|null|undefined} raw
 * @returns {null | {
 *   filter80Kw: boolean,
 *   filter160Kw: boolean,
 *   cheapestFilter: boolean,
 *   carFilter: 'all'|'china'|'european',
 *   portFilter: 'all'|'fast'|'slow',
 *   onlineFilter: boolean,
 *   nacsFilter: boolean,
 * }}
 * `null` when the param is absent/empty (caller keeps localStorage / defaults).
 */
export function parseMapFilterParam(raw) {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  const tokens = trimmed
    .split(',')
    .map(part => part.trim().toLowerCase())
    .filter(part => MAP_FILTER_TOKENS.has(part));

  if (tokens.length === 0) return null;

  const set = new Set(tokens);
  return {
    filter80Kw: set.has('80kw') || set.has('160kw') || set.has('nacs'),
    filter160Kw: set.has('160kw'),
    cheapestFilter: set.has('cheapest'),
    carFilter: set.has('china') ? 'china' : set.has('european') ? 'european' : 'all',
    portFilter: set.has('fast') ? 'fast' : set.has('slow') ? 'slow' : 'all',
    onlineFilter: set.has('online'),
    nacsFilter: set.has('nacs'),
  };
}

/**
 * Serialize active map filters to a `mapFilter` query value, or `null` when none.
 * @param {{
 *   filter80Kw?: boolean,
 *   filter160Kw?: boolean,
 *   cheapestFilter?: boolean,
 *   carFilter?: string,
 *   portFilter?: string,
 *   onlineFilter?: boolean,
 *   nacsFilter?: boolean,
 * }} filters
 * @returns {string|null}
 */
export function buildMapFilterParam(filters = {}) {
  const {
    filter80Kw = false,
    filter160Kw = false,
    cheapestFilter = false,
    carFilter = 'all',
    portFilter = 'all',
    onlineFilter = false,
    nacsFilter = false,
  } = filters;

  const parts = [];
  if (portFilter === 'fast' || portFilter === 'slow') parts.push(portFilter);
  if (carFilter === 'china' || carFilter === 'european') parts.push(carFilter);
  if (cheapestFilter) parts.push('cheapest');
  if (filter80Kw || filter160Kw || nacsFilter) parts.push('80kw');
  if (nacsFilter) parts.push('nacs');
  if (filter160Kw) parts.push('160kw');
  if (onlineFilter) parts.push('online');
  return parts.length > 0 ? parts.join(',') : null;
}

/**
 * Apply `mapFilter` to an existing URLSearchParams (preserves lang and other params).
 * @param {URLSearchParams} searchParams
 * @param {string|null} mapFilterValue
 * @returns {URLSearchParams}
 */
export function withMapFilterSearchParams(searchParams, mapFilterValue) {
  const next = new URLSearchParams(searchParams);
  if (mapFilterValue) {
    next.set('mapFilter', mapFilterValue);
  } else {
    next.delete('mapFilter');
  }
  return next;
}

/**
 * Keep the current map filters and set or clear the selected station.
 * Example: `/?mapFilter=80kw&station=8697`.
 * @param {URLSearchParams|string} searchParams
 * @param {string|number|null|undefined} stationKey
 * @returns {URLSearchParams}
 */
export function withSelectedStationSearchParams(searchParams, stationKey) {
  const next = new URLSearchParams(searchParams);
  const key = stationKey == null ? '' : String(stationKey).trim();
  if (key) {
    next.set('station', key);
  } else {
    next.delete('station');
  }
  return next;
}

/**
 * Absolute homepage URL for the selected map station, preserving mapFilter and other params.
 * Pass `baseUrl` in tests. In the browser the current origin and path are used.
 */
export function buildMapStationShareUrl(stationKey, search = '', baseUrl) {
  const params = withSelectedStationSearchParams(search, stationKey);
  const origin = baseUrl || (typeof window !== 'undefined' ? window.location.origin : 'https://220-km.com');
  const path = baseUrl ? '/' : typeof window !== 'undefined' ? window.location.pathname || '/' : '/';
  const query = params.toString();
  return `${origin}${path}${query ? `?${query}` : ''}`;
}
